import BaseModal from 'src/components/common/Modal/Modal/Modal'
import { UIButton } from 'src/components/common/uiButton/UIButton'
import { Tabs, Flex } from 'antd'
import styles from './ExpertsProfileModal.module.less'
import Tag from 'src/components/common/tag/Tag'
import redirectIcon from 'src/assets/icons/redirect.svg'
import SkillsTable from 'src/pages/experts/expertsProfileModal/skillsTable/SkillsTable'
import React, { useCallback, useState, useMemo, useEffect } from 'react'
import SearchBox from 'src/components/common/searchBox/SearchBox'
import { ExpertsProfileDataRespType, ExpertProfileSkillDataRespType } from 'src/types/api.types'
import { ExpertDetailsMapping } from 'src/constants/experts.constants'
import { LifeCycleStageColorMapping } from 'src/types/expert.types'
import { Loader } from 'src/components/common/loader/Loader'
import { DataTestId, TextConstants } from 'src/constants/ui.constants'
import { debounce } from 'lodash'
import SegmentAnalytics from 'src/SegmentAnalytics'

const {
    Close,
    ExpertsProfile,
    FetchingData,
    Qualified,
    TalentStudioProfile,
    PersonalInformation,
    Skills,
} = TextConstants.ExpertsProfileModal

export type ExpertsProfileModalProps = {
    open: boolean
    onCancel: () => void
    expertsProfile: ExpertsProfileDataRespType | undefined
    isLoading: boolean
}

const ExpertsProfileModal: React.FC<ExpertsProfileModalProps> = ({
    expertsProfile,
    open,
    onCancel,
    isLoading,
}) => {
    const [isSkillsVisible, setIsSkillsVisible] = useState<ExpertProfileSkillDataRespType[]>(
        expertsProfile?.skills ?? []
    )

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleSearchEvent = useCallback(
        debounce((value: string) => {
            SegmentAnalytics.sendTrackEvent('ExpertsProfile_Input_Text', {
                searchValue: value,
            })
        }, 1500),
        []
    )

    const handleSearch = useCallback(
        (value: string) => {
            setIsSkillsVisible(
                expertsProfile?.skills.filter(skill =>
                    skill.skills.toLowerCase().includes(value.toLowerCase().trim())
                ) ?? []
            )
            value && handleSearchEvent(value)
        },
        [expertsProfile?.skills, handleSearchEvent]
    )

    useEffect(() => {
        setIsSkillsVisible(expertsProfile?.skills ?? [])
    }, [expertsProfile?.skills])

    const generalInformationSection = useMemo(() => {
        return (
            <div className={styles.generalInformationWrapper}>
                <div className={styles.keyValueGrid}>
                    {Object.entries(expertsProfile?.expertDetails ?? {}).map(([key, value]) => {
                        return ExpertDetailsMapping[key] ? (
                            <React.Fragment key={key}>
                                <p className={styles.generalInformationKey}>
                                    {ExpertDetailsMapping[key]}
                                </p>
                                <p className={styles.generalInformationValue}>
                                    {typeof value === 'string' ? value : value.join(', ')}
                                </p>
                            </React.Fragment>
                        ) : null
                    })}
                </div>
                <p className={`${styles.generalInformationKey} ${styles.educationDetails}`}>
                    Education details
                </p>
                <p className={styles.generalInformationValue}>
                    <Flex vertical gap={'8px'}>
                        {expertsProfile?.expertDetails.education.map(
                            (education: { degree: string; major: string }) => (
                                <Flex
                                    key={`${education.degree}-${education.major}`}
                                    gap={'8px'}
                                    align={'flex-start'}
                                >
                                    <Tag label={education.degree} isRounded color="gray" fitWidth />
                                    <span>
                                        {education.major.length > 0 ? education.major : Qualified}
                                    </span>
                                </Flex>
                            )
                        )}
                    </Flex>
                </p>
            </div>
        )
    }, [expertsProfile?.expertDetails])

    const areasOfExpertiseSection = useMemo(() => {
        const style =
            (expertsProfile?.areasOfExpertise?.length ?? 0) >= 4 ? styles.grid : styles.flex
        return (
            <div className={styles.areasOfExpertiseWrapper}>
                <h4 className={styles.areasOfExpertiseTitle}>Areas of expertise</h4>
                <div className={style}>
                    {expertsProfile?.areasOfExpertise?.map(area => {
                        return (
                            <Tag
                                key={area}
                                label={area}
                                isRounded
                                textWrap="wrap"
                                textAlign="left"
                                color="white"
                            />
                        )
                    })}
                </div>
            </div>
        )
    }, [expertsProfile?.areasOfExpertise])

    const personalInformationTab = useMemo(() => {
        if (isLoading) {
            return <Loader title={FetchingData} />
        }
        return (
            <Flex justify={'space-between'} gap={'36px'}>
                {generalInformationSection}
                {areasOfExpertiseSection}
            </Flex>
        )
    }, [generalInformationSection, areasOfExpertiseSection, isLoading])

    const skillsTab = useMemo(() => {
        return (
            <div className={styles.skillTab}>
                <SearchBox
                    placeholder="Search skill"
                    className={styles.searchBox}
                    onChange={handleSearch}
                />
                <SkillsTable
                    dataSource={isSkillsVisible.map((skill, index) => ({
                        key: index,
                        skill: skill.skills,
                        level: skill.level,
                        score: skill.score,
                        skillValidatedBy: skill.skillValidatedBy,
                        validationLog: skill.validationLog,
                    }))}
                    isLoading={isLoading}
                />
            </div>
        )
    }, [isSkillsVisible, handleSearch, isLoading])

    const profileLink = useMemo(
        () => expertsProfile?.talentStudioProfileLink ?? '#',
        [expertsProfile]
    )

    const handleClick = useCallback(() => {
        SegmentAnalytics.sendTrackEvent('ExpertsProfile_TalentStudioProfileLinkCTA_Clicked', {
            talentStudioProfileLink: expertsProfile?.talentStudioProfileLink,
        })
        window.open(profileLink, '_blank')
    }, [profileLink, expertsProfile])

    return (
        <div data-testid={DataTestId.ExpertsProfileModal}>
            <BaseModal
                open={open}
                modalHeader={<span className={styles.expertProfileTitle}>{ExpertsProfile}</span>}
                modalFooter={<UIButton title={Close} onClick={onCancel} />}
                onCancel={onCancel}
                width={'70vw'}
                destroyOnClose
            >
                <div className={styles.modalWrapper}>
                    <Flex
                        justify={'space-between'}
                        align={'center'}
                        className={styles.expertHeader}
                        gap={'10px'}
                    >
                        <Flex align={'center'} gap={'16px'}>
                            <h2 className={styles.expertName}>
                                {expertsProfile?.expertDetails.name}
                            </h2>
                            <Tag
                                label={expertsProfile?.expertDetails.lifeCycleStage ?? ''}
                                isRounded
                                isUppercase
                                color={
                                    LifeCycleStageColorMapping[
                                        expertsProfile?.expertDetails.lifeCycleStage ?? ''
                                    ]
                                }
                                textWeight="medium"
                            />
                        </Flex>
                        <a href={profileLink} target="_blank" rel="noopener noreferrer">
                            <UIButton
                                title={TalentStudioProfile}
                                icon={redirectIcon}
                                width={'200px'}
                                className={styles.talentStudioProfileButton}
                                onClick={handleClick}
                            />
                        </a>
                    </Flex>
                    <Tabs
                        items={[
                            {
                                key: 'personalInformation',
                                label: PersonalInformation,
                                children: personalInformationTab,
                            },
                            { key: 'skills', label: Skills, children: skillsTab },
                        ]}
                    />
                </div>
            </BaseModal>
        </div>
    )
}

export default ExpertsProfileModal
